import type { SlackGetUsersResponse, SlackSendInvoiceApprovalMessageRequestPayload, SlackSendInvoiceApprovalMessageResponse } from '@weft/api-definition';
import { SlackGetUsersResponseSchema } from '@weft/api-definition';
import { INTEGRATION_PROVIDERS } from '@weft/types';
import { withDefault } from '@weft/utils';
import type { QueryClient, UseMutationOptions } from 'react-query';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { getPrivateApi } from '../../services/api';
import { useActivatedIntegration } from '../use-activated-integration';
import type { CustomUseQueryOptions } from './requests-types/custom-use-query-options';
const SLACK_USERS_QUERY_KEY = 'slack-users';

export function useSlackGetUsers(options?: CustomUseQueryOptions<SlackGetUsersResponse>) {
  const isSlackActivated = useActivatedIntegration(INTEGRATION_PROVIDERS.slack, { suspense: true });
  const isEnabledInOptions = withDefault(options?.enabled, true);
  const enabled = isEnabledInOptions && isSlackActivated; 

  return useQuery(
    [SLACK_USERS_QUERY_KEY], async () => {
      return getSlackUsers();
    }, { ...options, enabled },
  );
}

export async function prefetchSlackUsers(queryClient: QueryClient) {
  await queryClient.prefetchQuery([SLACK_USERS_QUERY_KEY], () => getSlackUsers());
}

export async function getSlackUsers() {
  try {
    const privateApi = getPrivateApi();
    const slackUsers = await privateApi.getSlackUsers();

    return SlackGetUsersResponseSchema.parse(slackUsers);
  } catch (error) {
    console.error('Error fetching Slack users:', error);

    return { users: [] };
  }
}

export function useSlackAuthRedirect() {
  const queryClient = useQueryClient();

  return useMutation(async () => {
    const api = getPrivateApi();
    const redirectResponse = await api.slackConnect({ redirectUrl: window.location.href });

    await queryClient.invalidateQueries('connectedIntegrations');
    window.location.href = redirectResponse.redirectUrl;
  });
}

export function useShareInvoiceToSlack(
  options?: UseMutationOptions<SlackSendInvoiceApprovalMessageResponse, unknown, SlackSendInvoiceApprovalMessageRequestPayload>,
) {
  const api = getPrivateApi();

  return useMutation<SlackSendInvoiceApprovalMessageResponse, unknown, SlackSendInvoiceApprovalMessageRequestPayload>(
    async ({ userId, invoice }) => {
      const response = await api.sendSlackInvoiceApprovalMessage({ slackSendInvoiceApprovalMessageRequestSchema: { userId, invoice } });

      return response;
    },
    options,
  );
}